"use client"

import * as React from "react"

import { useMediaQuery } from "@/hooks/use-media-query"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

type Channel = {
  value: string
  label: string
  id: string
  avatar?: string
}

const channels: Channel[] = [
  {
    value: "all",
    label: "All Channels",
    id: "",
    avatar: "/channel-avatars/all.svg",
  },
  {
    value: "pewdiepie",
    label: "PewDiePie",
    id: "UC-lHJZR3Gqxm24_Vd_AJ5Yw",
    avatar: "/channel-avatars/pewdiepie.jpg",
  },
  {
    value: "mrbeast",
    label: "MrBeast",
    id: "UCX6OQ3DkcsbYNE6H8uQQuVA",
    avatar: "/channel-avatars/mrbeast.jpg",
  },
  {
    value: "markiplier",
    label: "Markiplier",
    id: "UC7_YxT-KID8kRbqZo7MyscQ",
    avatar: "/channel-avatars/markiplier.jpg",
  },
  {
    value: "dude-perfect",
    label: "Dude Perfect",
    id: "UCRijo3ddMTht_IHyNSNXpNQ",
    avatar: "/channel-avatars/dude-perfect.jpg",
  },
  {
    value: "cocomelon",
    label: "Cocomelon",
    id: "UCbCmjCuTUZos6Inko4u57UQ",
    avatar: "/channel-avatars/cocomelon.jpg",
  },
  {
    value: "t-series",
    label: "T-Series",
    id: "UCq-Fj5jknLsUf-MWSy4_brA",
    avatar: "/channel-avatars/t-series.jpg",
  },
  {
    value: "5-minute-crafts",
    label: "5-Minute Crafts",
    id: "UC295-Dw_tDNtZXFeAPAW6Aw",
    avatar: "/channel-avatars/5-minute-crafts.jpg",
  },
  {
    value: "wwe",
    label: "WWE",
    id: "UCJ5v_MCY6GNUBTO8-D3XoAg",
    avatar: "/channel-avatars/wwe.jpg",
  },
  {
    value: "gaming-food",
    label: "Gaming Food Ideas",
    id: "UCmGSJVG3mCRXVOP4yZrU1Dw",
    avatar: "/channel-avatars/gaming-food.jpg",
  },
  {
    value: "bright-side",
    label: "BRIGHT SIDE",
    id: "UC4rlAVgAK0SGk-yTfe48Qpw",
    avatar: "/channel-avatars/bright-side.jpg",
  },
  {
    value: "linus-tech-tips",
    label: "Linus Tech Tips",
    id: "UCXuqSBlHAE6Xw-yeJA0Tunw",
    avatar: "/channel-avatars/linus-tech-tips.jpg",
  },
  {
    value: "mkbhd",
    label: "Marques Brownlee",
    id: "UCBJycsmduvYEL83R_U4JriQ",
    avatar: "/channel-avatars/mkbhd.jpg",
  },
  {
    value: "veritasium",
    label: "Veritasium",
    id: "UCHnyfMqiRRG1u-2MsSQLbXA",
    avatar: "/channel-avatars/veritasium.jpg",
  },
  {
    value: "kurzgesagt",
    label: "Kurzgesagt",
    id: "UCsXVk37bltHxD1rDPwtNM8Q",
    avatar: "/channel-avatars/kurzgesagt.jpg",
  },
]

interface ComboBoxResponsiveProps {
  selectedChannel: Channel | null;
  onChannelChange: (channel: Channel | null) => void;
}

export function ComboBoxResponsive({ selectedChannel, onChannelChange }: ComboBoxResponsiveProps) {
  const [open, setOpen] = React.useState(false)
  const isDesktop = useMediaQuery("(min-width: 768px)")

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="min-w-[140px] justify-start h-[38px] gap-2">
            {selectedChannel ? (
              <>
                {selectedChannel.avatar && (
                  <img 
                    src={selectedChannel.avatar} 
                    alt={selectedChannel.label}
                    className="w-5 h-5 rounded-full object-cover"
                  />
                )}
                <span>{selectedChannel.label}</span>
              </>
            ) : (
              <>+ Set channel</>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <ChannelList setOpen={setOpen} setSelectedChannel={onChannelChange} />
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="outline" className="min-w-[140px] justify-start h-[38px] gap-2">
          {selectedChannel ? (
            <>
              {selectedChannel.avatar && (
                <img 
                  src={selectedChannel.avatar} 
                  alt={selectedChannel.label}
                  className="w-5 h-5 rounded-full object-cover"
                />
              )}
              <span>{selectedChannel.label}</span>
            </>
          ) : (
            <>+ Set channel</>
          )}
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mt-4 border-t">
          <ChannelList setOpen={setOpen} setSelectedChannel={onChannelChange} />
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function ChannelList({
  setOpen,
  setSelectedChannel,
}: {
  setOpen: (open: boolean) => void
  setSelectedChannel: (channel: Channel | null) => void
}) {
  return (
    <Command>
      <CommandInput placeholder="Search channels..." className="h-[28px] focus:outline-none focus:ring-0 border-0 focus:border-0"/>
      <CommandList>
        <CommandEmpty>No channels found.</CommandEmpty>
        <CommandGroup>
          {channels.map((channel) => (
            <CommandItem
              key={channel.value}
              value={channel.value}
              onSelect={(value) => {
                setSelectedChannel(
                  channels.find((ch) => ch.value === value) || null
                )
                setOpen(false)
              }}
              className="flex items-center gap-2"
            >
              {channel.avatar && (
                <img 
                  src={channel.avatar} 
                  alt={channel.label}
                  className="w-5 h-5 rounded-full object-cover"
                />
              )}
              <span>{channel.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}